import { useMemo, useState, type ReactNode } from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';

export interface SearchOption<V extends string | number = string> { value: V; label: string; hint?: string }

/** Lists up to this size open in full; longer ones (countries, states, cities…) are searched by typing. */
export const SHORT_LIST = 12;
/** At most this many matches are shown at a time. */
const MAX_SHOWN = 50;

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

/**
 * Matches for what was typed, best first: names that start with it ("him" → Himachal Pradesh), then a word that starts
 * with it ("pra" → Uttar Pradesh), then anywhere in the name or its hint (a code such as "IN").
 */
export function rankOptions<V extends string | number>(options: SearchOption<V>[], typed: string, max = MAX_SHOWN): SearchOption<V>[] {
  const q = norm(typed);
  if (!q) return options.slice(0, max);
  const starts: SearchOption<V>[] = [], words: SearchOption<V>[] = [], inside: SearchOption<V>[] = [];
  for (const o of options) {
    const l = norm(o.label);
    if (l.startsWith(q)) starts.push(o);
    else if (l.split(/[\s,()/-]+/).some((w) => w.startsWith(q))) words.push(o);
    else if (l.includes(q) || (o.hint && norm(o.hint).includes(q)) || norm(String(o.value)) === q) inside.push(o);
  }
  return [...starts, ...words, ...inside].slice(0, max);
}

/**
 * A dropdown you type into (country, state, city and other long lists): type 2–3 letters and only the matching names
 * show, best first; pick one. No scrolling through a long list. A short list (up to 12) still opens in full on click.
 */
export default function SearchSelect<V extends string | number = string>({
  options, value, onChange, label, placeholder, id, size = 'small', error, helperText, disabled, required, fullWidth = true, loading,
  clearable = true, sx,
}: {
  options: SearchOption<V>[]; value: V | null | undefined | ''; onChange: (v: V | null) => void; label?: string; placeholder?: string; id?: string;
  size?: 'small' | 'medium'; error?: boolean; helperText?: ReactNode; disabled?: boolean; required?: boolean; fullWidth?: boolean; loading?: boolean;
  clearable?: boolean; sx?: object;
}) {
  const [typed, setTyped] = useState('');
  const short = options.length <= SHORT_LIST;
  const current = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);
  const hint = short ? 'Nothing matches' : typed.trim().length === 0 ? 'Type 2–3 letters to search' : 'Nothing matches. Check the spelling.';
  return (
    <Autocomplete<SearchOption<V>, false, boolean, false>
      id={id} size={size} fullWidth={fullWidth} disabled={disabled} loading={loading} sx={sx}
      options={options} value={current} disableClearable={!clearable || required}
      inputValue={typed !== '' || !current ? typed : current.label}
      onInputChange={(_, v, reason) => { if (reason === 'input' || reason === 'clear') setTyped(v); if (reason === 'reset') setTyped(''); }}
      onChange={(_, o) => { setTyped(''); onChange(o ? o.value : null); }}
      // A long list shows nothing until something is typed, then only the matches.
      filterOptions={(opts, state) => (short ? rankOptions(opts, state.inputValue === current?.label ? '' : state.inputValue, opts.length)
        : (state.inputValue.trim() && state.inputValue !== current?.label) ? rankOptions(opts, state.inputValue) : [])}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      noOptionsText={hint}
      autoHighlight
      openOnFocus={short}
      renderOption={(props, o) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <li key={key} {...rest}>
            <Box>
              {o.label}
              {o.hint && <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, ml: 0.75 }}>{o.hint}</Box>}
            </Box>
          </li>
        );
      }}
      renderInput={(p) => (
        <TextField {...p} label={label} required={required} placeholder={placeholder ?? (short ? undefined : 'Type to search…')} error={error} helperText={helperText} />
      )}
    />
  );
}

/** Shown in a long list's popup before anything is typed. */
export const TYPE_TO_SEARCH = 'Type 2–3 letters to search';

/**
 * The same type-ahead for an Autocomplete that keeps its own options (multi-select, free text, "+ Add new"): a long
 * list shows nothing until something is typed, then the matches best first; a short list shows everything.
 */
export function typeAhead<T>(label: (o: T) => string, hint?: (o: T) => string | undefined) {
  return (options: T[], state: { inputValue: string }): T[] => {
    const typed = state.inputValue.trim();
    if (!typed) return options.length <= SHORT_LIST ? options : [];
    const ranked = rankOptions(options.map((o, i) => ({ value: i, label: label(o), hint: hint?.(o) })), typed);
    return ranked.map((r) => options[r.value]);
  };
}
